// kfe-pathway.js — squiggle pathway screen, level-aware.
//
// PORT TARGET: Assets/KFE/Scripts/Runtime/Core/UI/Pathway/PathwayScreen.cs
//
// Two routes share this component, parameterised by `level`:
//   /                  → level="stage"     (orange path, stages of the course)
//   /stage/<stageId>   → level="lesson"    (cyan path, lessons inside that stage)
//
// Tap on a stage navigates to /stage/<stageId>; tap on a lesson navigates
// to /lesson/<lessonId>. The squiggle component itself is dumb about the
// level — it just renders what we hand it. The level-specific stroke
// colour is applied via the squiggle's `level` prop.
//
// Brief 022: data comes from the pathway loader (`/content/kfe_pathways.json`
// + per-pathway tree json). Gates inline in the data are filtered out of
// the rendered nodes but still participate in locking via
// `pathwayFromOrderedNodes`. Multi-pathway switching shipped in Brief 157: the
// active pathway is read from getActivePathwayId() (localStorage `kfe_pathway_id`,
// default ZTF) and this screen reloads on the `kfe:pathway-changed` event.
//
// Brief 032: intro lessons (`isIntro: true`) are stripped from the squiggle
// (so numbering stays "node N = Lesson N") and surfaced as a separate row
// list above the squiggle. Each row is tappable and navigates to the same
// `/lesson/<id>` route as a numbered lesson. Locked intros (gateRule)
// render at reduced opacity and don't navigate. iOS canon: stage-overview
// affordance in LessonChartViewController.m / sibling stage screens.

import { KFEComponent } from "../core/component.js";
import { html } from "../core/html.js";
import { t, getLanguage } from "../core/i18n.js";
import { services } from "../platform-services.js";
import { pathwayFromOrderedNodes, deriveStageComplete, deriveLessonComplete } from "../data/pathway-builder.js";
import { loadPathway } from "../data/pathway-loader.js";
import {
  buildSingleFolderTree,
  getInaccessibleItems,
} from "../data/gating.js";
import { isGatingExemptId } from "../data/gating-config.js";
import { getActivePathwayId } from "../core/active-pathway.js";
import { navigate } from "../router.js";

/**
 * @typedef {object} IntroRow
 * @property {string|number} id
 * @property {string} titleEn
 * @property {string} [titleZh]
 * @property {import("../data/gating.js").GateRule} [gateRule]
 */

/**
 * One squiggle node plus its roll-up derivation (Brief 163, Fix 1 / Option A).
 * `node` is the ordered node WITHOUT a `derivedComplete` decision — that is
 * supplied either from the persisted cache (first paint) or by running `derive`
 * in the background. `derive` is null for gate / gating-exempt nodes, which are
 * never required children of any roll-up and read their state the old way.
 *
 * @typedef {object} RollupEntry
 * @property {string|number} id
 * @property {import("../data/pathway-builder.js").OrderedPathwayNode} node
 * @property {(() => Promise<boolean>) | null} derive
 */

export class KFEPathway extends KFEComponent {
  static props = ["level", "stageId"];
  static styles = `
    :host { display: block; }
    .empty { color: var(--kfe-muted); padding: 2rem; text-align: center; }

    /* Intro affordance (Brief 032). Sits above the squiggle when the
       current stage has one or more lessons flagged isIntro=true. */
    .intros {
      list-style: none;
      padding: 0 1rem;
      margin: 0.5rem auto 1rem;
      max-width: 540px;
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
    }
    .intro {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      padding: 0.75rem 1rem;
      background: var(--kfe-surface);
      border: 1px solid var(--kfe-border);
      border-radius: 8px;
      cursor: pointer;
      color: var(--kfe-fg);
      font-family: var(--kfe-font);
    }
    .intro:hover { background: var(--kfe-surface-hover); }
    .intro:focus-visible {
      outline: 2px solid var(--kfe-line-lesson, #23a4d9);
      outline-offset: 2px;
    }
    .intro.locked { opacity: var(--kfe-locked-opacity); cursor: not-allowed; }
    .intro-title {
      flex: 1;
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .intro-badge {
      color: #fff;
      background: var(--kfe-accent-cyan, var(--kfe-line-lesson, #23a4d9));
      font-size: 0.7rem;
      font-weight: 600;
      text-transform: uppercase;
      padding: 2px 8px;
      border-radius: 4px;
      white-space: nowrap;
    }

    /* Horizontal scroll-snap pages — no JS gesture handling. */
    .pages {
      display: grid;
      grid-auto-flow: column;
      grid-auto-columns: 100%;
      overflow-x: auto;
      scroll-snap-type: x mandatory;
      scrollbar-width: none;
      -webkit-overflow-scrolling: touch;
    }
    .pages::-webkit-scrollbar { display: none; }
    .pages > kfe-squiggle {
      scroll-snap-align: start;
      padding-block: 0.5rem;
      justify-self: center;
    }

    /* Page indicator dots. Driven by IntersectionObserver below. */
    nav.page-dots {
      display: flex;
      justify-content: center;
      gap: 0.5rem;
      margin-top: 0.75rem;
    }
    nav.page-dots span {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: var(--kfe-muted);
      opacity: 0.4;
      transition: opacity 0.15s, background 0.15s;
    }
    nav.page-dots span.active {
      background: var(--kfe-accent);
      opacity: 1;
    }
  `;

  /** @type {import("../data/pathway-builder.js").PathwayPage[]} */
  _pages = [];
  /** @type {IntersectionObserver | null} */
  _observer = null;
  _activePage = 0;
  /** @type {"stage" | "lesson"} */
  _level = "stage";
  _gatingEnabled = true;
  /** @type {IntroRow[]} */
  _intros = [];
  /** @type {Set<string>} */
  _introLocked = new Set();
  /** False until onConnected has finished loading the pathway. Distinguishes
   *  the initial load (show "Loading…") from a genuinely empty pathway (show
   *  the empty/diagnostic message). Brief 122, Symptom A. */
  _loaded = false;
  /** Brief 168 dispatch fork: set to the pathway id when the active pathway is a
   *  flat (menu) shape — no `stages`, only `items`. render() then delegates to
   *  <kfe-pathway-list> instead of the squiggle. Null for squiggle pathways. */
  /** @type {string | null} */
  _flatPathwayId = null;

  async onConnected() {
    const onI18n = () => this._scheduleRender();
    document.addEventListener("kfe:i18n", onI18n);
    this.addCleanup(() => document.removeEventListener("kfe:i18n", onI18n));

    // Brief 157: re-load against the new pathway when the learner switches study
    // paths while this screen is mounted. The primary switch flow navigates to
    // "/" (a fresh mount that reads getActivePathwayId on connect); this handler
    // covers the both-levels mounted-screen case. addCleanup per the
    // event-handler-stacking gotcha (CLAUDE.md Common Pitfall #1).
    const onPathwayChanged = () => { void this._loadActivePathway(); };
    document.addEventListener("kfe:pathway-changed", onPathwayChanged);
    this.addCleanup(() => document.removeEventListener("kfe:pathway-changed", onPathwayChanged));

    await this._loadActivePathway();
  }

  /**
   * Load the active pathway (Brief 157: localStorage-backed getActivePathwayId)
   * and paint the squiggle for this screen's level. Re-runnable: invoked on
   * connect and again on `kfe:pathway-changed`.
   */
  async _loadActivePathway() {
    const level = /** @type {any} */ (this).level === "lesson" ? "lesson" : "stage";
    const stageId = /** @type {string|undefined} */ (/** @type {any} */ (this).stageId);

    const pathwayId = getActivePathwayId();

    let headerTitle = t("pathway.title");
    /** @type {RollupEntry[]} */
    let entries = [];
    /** @type {IntroRow[]} */
    let intros = [];
    /** @type {Set<string>} */
    let introLocked = new Set();

    try {
      const pathway = await loadPathway(pathwayId);
      headerTitle = pathway.titleEn || headerTitle;

      // Brief 168 dispatch fork: iOS picks the home VC by pathway shape —
      // StagesViewController (squiggle) vs TableListViewController (flat menu).
      // A flat pathway has no `stages`, only `items`; render the list mirror
      // (<kfe-pathway-list>) instead of the squiggle. The web router mounts one
      // tag per route, so kfe-pathway delegates here rather than introducing a
      // third dispatcher component at "/". Only at stage level: flat pathways
      // have no stages, so /stage/:id is never reached for them.
      if (
        level === "stage" &&
        !(pathway.stages && pathway.stages.length) &&
        Array.isArray(/** @type {any} */ (pathway).items) &&
        /** @type {any} */ (pathway).items.length
      ) {
        this._flatPathwayId = pathwayId;
        this._intros = [];
        this._level = level;
        this._loaded = true;
        this.dispatchEvent(new CustomEvent("kfe:set-header-title", {
          detail: { title: headerTitle },
          bubbles: true,
          composed: true,
        }));
        this._scheduleRender();
        return;
      }
      this._flatPathwayId = null;

      if (level === "stage") {
        // Brief 151/153: a stage's "completed" signal is DERIVED from its
        // lessons' per-item progress (Option A — no persisted roll-up ROW).
        // Without it every stage reads completedCount 0, so no "End Stage N
        // Gate" can open and Stage 3+ stay sealed.
        //
        // Brief 163: that derive must NOT gate first paint. Each
        // deriveStageComplete fetches every lesson JSON in the stage — on a cold
        // cache that is a 50+ request fetch storm rooted right here (the brief's
        // DevTools call stack: kfe-pathway → deriveStageComplete →
        // deriveLessonComplete → loadLesson). So we no longer await the derive
        // before paint. Each stage becomes a RollupEntry whose `derive` is run by
        // _buildAndPaint: the first paint uses the PERSISTED roll-up cache (no
        // fetch); stale/missing entries recompute in the background and re-render.
        // Gate / exempt stages aren't required children of any roll-up → no derive.
        entries = (pathway.stages || []).map((s) => ({
          id: s.id,
          node: stageEntryToNode(s),
          derive: (s.type !== "gate" && !isGatingExemptId(s.id, /** @type {any} */ (s).excludeFromGating))
            ? () => deriveStageComplete(pathwayId, s)
            : null,
        }));
      } else if (stageId) {
        const stage = (pathway.stages || []).find(
          /** @param {import("../data/pathway-loader.js").PathwayStage} s */
          (s) => s.id === stageId,
        );
        if (stage) {
          headerTitle = stage.titleEn || t("pathway.stageTitle", { id: String(stage.order ?? "?") });
          const lessons = stage.lessons || [];
          // Brief 032: intros surface in a separate row list above the
          // squiggle. pathwayFromOrderedNodes already drops them from the
          // numbered nodes via isIntroItem; here we capture them in pathway
          // order so the affordance can render them.
          intros = lessons
            .filter((l) => l && l.isIntro === true && l.type !== "gate")
            .map((l) => ({
              id: l.id,
              titleEn: l.titleEn ?? "",
              titleZh: l.titleZh,
              gateRule: /** @type {any} */ (l.gateRule),
            }));
          // Brief 153: per-lesson completion derives the same way Brief 151
          // derives per-stage completion — render the iOS-canonical "completed"
          // green on the cyan squiggle once every visible required item is done.
          //
          // iOS canon (read per Rule #9 — see also kfe-squiggle.js' node-state
          // comment): `LessonsViewController.m` runs `redrawSquiggles` (locked →
          // grey, accessible → white) then `updateSquigglesCompletedItemsToGreen`,
          // which OVERRIDES every `completed==1` node to green #8CC63F — a
          // learner-progress signal carrying NO gating check, so it derives
          // REGARDLESS of gatingEnabled.
          //
          // Brief 163: same paint-first treatment as the stage branch — cached on
          // first paint, recomputed in the background (no `lesson_*.json` fetch on
          // the render path). The exempt guard is a defensive no-op today (only
          // stage_143095 is exempt) kept for future per-lesson flags.
          entries = lessons.map((l) => ({
            id: l.id,
            node: lessonEntryToNode(l),
            derive: (l.type !== "gate" && !isGatingExemptId(l.id, /** @type {any} */ (l).excludeFromGating))
              ? () => deriveLessonComplete(pathwayId, l)
              : null,
          }));

          // Locked-state for intros: reuse the gating module against the
          // stage's whole lesson list so any gateRule on an intro is
          // honoured. Operates on the already-loaded pathway tree — no lesson
          // fetch, so it stays on the first-paint path.
          const gatingEnabled = (await services.learner.getSetting("gating_enabled")) !== false;
          if (gatingEnabled && intros.length > 0) {
            /** @type {import("../data/gating.js").ItemNode[]} */
            const nodes = lessons.map((l) => ({
              id: l.id,
              isGate: l.type === "gate",
              isIntroItem: l.isIntro === true,
              isGatingExempt: isGatingExemptId(l.id, /** @type {any} */ (l).excludeFromGating),
              gateRule: /** @type {any} */ (l.gateRule),
            }));
            const folderId = "__stage_" + String(stage.id);
            const tree = buildSingleFolderTree(folderId, nodes);
            const ids = getInaccessibleItems(folderId, {
              gatingEnabled: true,
              completedCounts: {},
            }, tree);
            introLocked = new Set(ids.map(String));
          }
        } else {
          console.warn(`[pathway] stage not found in ${pathwayId}: ${stageId}`);
        }
      }
    } catch (e) {
      console.warn("[pathway] failed to load pathway", e);
    }
    this._intros = intros;
    this._introLocked = introLocked;
    this._level = level;
    this._gatingEnabled = (await services.learner.getSetting("gating_enabled")) !== false;

    this.dispatchEvent(new CustomEvent("kfe:set-header-title", {
      detail: { title: headerTitle },
      bubbles: true,
      composed: true,
    }));

    // Brief 163: paint NOW from the persisted roll-up cache (zero lesson
    // fetches), then recompute stale entries off the critical path.
    await this._buildAndPaint(pathwayId, entries);
  }

  /**
   * Brief 163 (Fix 1 / Option A) — paint-first roll-up. Builds the squiggle
   * nodes from the PERSISTED roll-up cache so first paint awaits ZERO
   * `lesson_*.json` fetches (the cold-cache regression's root cause — see the
   * brief's call stack), paints, then recomputes any stale or missing entries in
   * the background and re-renders only if a completion result actually moved.
   *
   * @param {string} pathwayId
   * @param {RollupEntry[]} entries
   */
  async _buildAndPaint(pathwayId, entries) {
    const learner = /** @type {any} */ (services.learner);
    /** @type {Map<string, {id:string, tick:number, complete:boolean}>} */
    let cache = new Map();
    try {
      const rows = await learner.getAllRollupCache();
      cache = new Map(rows.map((/** @type {any} */ r) => [String(r.id), r]));
    } catch (e) {
      console.warn("[pathway] roll-up cache read failed; first paint assumes incomplete", e);
    }

    // Seed each derivable node's completion from cache (or false when no cache
    // entry yet). Gate / exempt nodes keep derivedComplete undefined → the
    // existing store-read path in pathwayFromOrderedNodes (unchanged).
    for (const e of entries) {
      if (!e.derive) continue;
      const c = cache.get(String(e.id));
      e.node.derivedComplete = c ? c.complete : false;
    }

    try {
      this._pages = await pathwayFromOrderedNodes(entries.map((e) => e.node));
    } catch (e) {
      console.warn("[pathway] failed to compute pathway pages", e);
      this._pages = [];
    }
    this._loaded = true;
    this._scheduleRender();
    queueMicrotask(() => this._observePages());

    this._scheduleRollupRefresh(pathwayId, entries, cache);
  }

  /**
   * Recompute stale / missing roll-up entries off the critical path, persist
   * them keyed by the current progress tick, and re-render only if a node's
   * completion changed from what first paint showed. Scheduled on idle so it
   * never contends with the user's first interaction or the deferred content
   * prefetch (Brief 163, Fix 2). Conservative on failure: a derive that throws
   * (e.g. a lesson JSON that can't load) counts as NOT complete — never
   * over-unlocks, mirroring deriveLessonComplete.
   *
   * @param {string} pathwayId
   * @param {RollupEntry[]} entries
   * @param {Map<string, {id:string, tick:number, complete:boolean}>} cache entries used for first paint
   */
  _scheduleRollupRefresh(pathwayId, entries, cache) {
    const learner = /** @type {any} */ (services.learner);
    const run = async () => {
      let currentTick = 0;
      try { currentTick = await learner.getProgressTick(); } catch { currentTick = 0; }
      let changed = false;
      for (const e of entries) {
        if (!e.derive) continue;
        if (!this._connected) return;
        const cached = cache.get(String(e.id));
        if (cached && cached.tick === currentTick) continue; // fresh — no fetch needed
        let complete = false;
        try { complete = await e.derive(); } catch { complete = false; }
        try { await learner.setRollupCacheEntry(e.id, currentTick, complete); } catch { /* cache is best-effort */ }
        const painted = cached ? cached.complete : false;
        if (complete !== painted) changed = true;
        e.node.derivedComplete = complete;
      }
      if (changed && this._connected) {
        try {
          this._pages = await pathwayFromOrderedNodes(entries.map((e) => e.node));
          this._scheduleRender();
          queueMicrotask(() => this._observePages());
        } catch (err) {
          console.warn("[pathway] roll-up refresh re-render failed", err);
        }
      }
    };
    const schedule = (typeof requestIdleCallback === "function")
      ? requestIdleCallback
      : /** @param {() => void} cb */ (cb) => setTimeout(cb, 0);
    schedule(() => { run().catch((err) => console.warn("[pathway] roll-up refresh failed", err)); });
  }

  onDisconnected() {
    this._observer?.disconnect();
    this._observer = null;
  }

  _observePages() {
    this._observer?.disconnect();
    const root = /** @type {HTMLElement | null} */ (this.shadowRoot?.querySelector(".pages"));
    if (!root) return;
    this._observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        const idx = Number(/** @type {HTMLElement} */ (entry.target).dataset.pageIndex);
        if (Number.isFinite(idx)) this._setActivePage(idx);
      }
    }, { root, threshold: 0.6 });
    for (const sq of root.querySelectorAll("kfe-squiggle")) this._observer.observe(sq);
  }

  /** @param {number} idx */
  _setActivePage(idx) {
    if (idx === this._activePage) return;
    this._activePage = idx;
    const dots = this.shadowRoot?.querySelectorAll("nav.page-dots span");
    if (!dots) return;
    for (let i = 0; i < dots.length; i++) {
      dots[i].classList.toggle("active", i === idx);
    }
  }

  /** @param {Event} e */
  _onSelect = (e) => {
    const ce = /** @type {CustomEvent<{itemId: string|number}>} */ (e);
    if (!ce.detail) return;
    const id = String(ce.detail.itemId);
    if (this._level === "stage") {
      navigate("/stage/" + id);
    } else {
      navigate("/lesson/" + id);
    }
  };

  /** @param {IntroRow} intro */
  _renderIntro(intro) {
    const lang = getLanguage();
    const title = (lang === "zh-Hans" && intro.titleZh)
      ? intro.titleZh
      : (intro.titleEn || String(intro.id));
    const isLocked = this._introLocked.has(String(intro.id));

    const li = document.createElement("li");
    li.className = "intro" + (isLocked ? " locked" : "");
    li.setAttribute("role", "button");
    li.setAttribute("tabindex", isLocked ? "-1" : "0");
    li.setAttribute("aria-disabled", isLocked ? "true" : "false");
    li.setAttribute("aria-label", `${t("pathway.introBadge")}: ${title}`);

    const titleEl = document.createElement("span");
    titleEl.className = "intro-title";
    titleEl.textContent = title;

    const badge = document.createElement("span");
    badge.className = "intro-badge";
    badge.textContent = t("pathway.introBadge");

    li.append(titleEl, badge);

    if (!isLocked) {
      const open = () => navigate("/lesson/" + String(intro.id));
      li.addEventListener("click", open);
      li.addEventListener("keydown", (e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          open();
        }
      });
    }
    return li;
  }

  render() {
    // First render runs synchronously on connect, before onConnected has
    // loaded the pathway. Show "Loading…" rather than the empty/diagnostic
    // message — the latter looks like an error during normal boot (Brief 122).
    if (!this._loaded) {
      return html`<div class="empty">${t("loading")}</div>`;
    }
    // Brief 168 dispatch fork: flat (menu) pathway → delegate the whole screen
    // to the TableListViewController mirror. <kfe-pathway-list> owns all flat-list
    // logic (rows, drilldown, completion); kfe-pathway is just the router host.
    if (this._flatPathwayId) {
      const list = document.createElement("kfe-pathway-list");
      /** @type {any} */ (list).pathwayId = this._flatPathwayId;
      return html`${list}`;
    }
    if (this._pages.length === 0 && this._intros.length === 0) {
      return html`<div class="empty">${t("pathway.empty")}</div>`;
    }
    const level = this._level;
    const gatingEnabled = this._gatingEnabled;
    return html`
      ${this._intros.length > 0
        ? html`<ul class="intros" aria-label=${t("pathway.introListLabel")}>
            ${this._intros.map((intro) => this._renderIntro(intro))}
          </ul>`
        : ""}
      <div class="pages" on:select=${this._onSelect}>
        ${this._pages.map((page, i) => {
          const sq = document.createElement("kfe-squiggle");
          /** @type {any} */ (sq).page = page;
          /** @type {any} */ (sq).level = level;
          /** @type {any} */ (sq).gatingEnabled = gatingEnabled;
          sq.dataset.pageIndex = String(i);
          return sq;
        })}
      </div>
      ${this._pages.length > 1
        ? html`<nav class="page-dots" aria-label="Page indicator">${this._pages.map((_, i) => {
            const dot = document.createElement("span");
            if (i === this._activePage) dot.classList.add("active");
            return dot;
          })}</nav>`
        : ""}
    `;
  }
}

/**
 * @param {import("../data/pathway-loader.js").PathwayStage} s
 * @returns {import("../data/pathway-builder.js").OrderedPathwayNode}
 */
function stageEntryToNode(s) {
  return {
    itemId: s.id,
    title: s.titleEn ?? "",
    titleZh: s.titleZh,
    isGate: s.type === "gate",
    // Brief 150: thread the content exemption flag so the stage squiggle's
    // gating honours self-assessment stages (e.g. stage_143095). Runtime
    // fallback (the static id set) is applied inside pathwayFromOrderedNodes.
    excludeFromGating: /** @type {any} */ (s).excludeFromGating === true,
    gateRule: /** @type {any} */ (s.gateRule),
  };
}

/**
 * @param {import("../data/pathway-loader.js").PathwayLessonRef} l
 * @returns {import("../data/pathway-builder.js").OrderedPathwayNode}
 */
function lessonEntryToNode(l) {
  return {
    itemId: l.id,
    title: l.titleEn ?? "",
    titleZh: l.titleZh,
    isGate: l.type === "gate",
    isIntroItem: l.isIntro === true,
    // Brief 150: honour an exemption flag at lesson level too (defensive — the
    // ZTF exemption is on a stage id, but a CMS could flag a lesson later).
    excludeFromGating: /** @type {any} */ (l).excludeFromGating === true,
    gateRule: /** @type {any} */ (l.gateRule),
  };
}
